import React, { useState } from 'react';
import { Zap, ChevronDown, ChevronRight, Play, Trash2 } from 'lucide-react';
import { cn } from 'App/shadcn/lib/utils';
import T from 'Components/Helpers/translate';
import { notifyAIWorkspaceError } from './notifyAIWorkspaceError';

type EventHookDetailsState = {
	id?: number;
	label?: string;
	entityType?: string | null;
	event?: 'insert' | 'update' | null;
	filters?: Array<{ field: string; operator: string; value?: string | number | boolean | null }>;
	prompt?: string;
	conversationId?: number;
	enabled?: boolean;
};

const ENTITY_TYPE_LABELS: Record<string, string> = {
	Order: 'Deal',
	Activity: 'Activity',
	Appointment: 'Meeting',
	Contact: 'Contact'
};

const EVENT_LABELS: Record<string, string> = {
	insert: 'created',
	update: 'updated'
};

const FILTER_OPERATOR_LABELS: Record<string, string> = {
	equals: 'equals',
	not_equals: 'does not equal',
	contains: 'contains',
	not_contains: 'does not contain',
	greater_than: '>',
	less_than: '<',
	increased: 'increased',
	decreased: 'decreased',
	moved_forward: 'moved forward',
	moved_back: 'moved back'
};

const buildDryRunPrompt = (hook: EventHookDetailsState): string => {
	const entity = ENTITY_TYPE_LABELS[hook.entityType ?? ''] ?? hook.entityType ?? 'record';
	const event = EVENT_LABELS[hook.event ?? ''] ?? hook.event ?? 'changed';
	const filters = Array.isArray(hook.filters) ? hook.filters : [];
	const mockPayload: Record<string, unknown> = { id: 'mock-dry-run', name: `Test ${entity}` };
	for (const f of filters) {
		if (f.value !== undefined && f.value !== null && f.value !== '') {
			mockPayload[f.field] = f.value;
		}
	}
	return [
		`[Dry run] The "${entity} ${event}" event hook fired with a synthetic payload.`,
		`Mock ${entity}:`,
		'```json',
		JSON.stringify(mockPayload, null, 2),
		'```',
		'',
		'Please run the hook prompt below against this mock data. Mark the output clearly as a test so the user knows nothing was sent or saved.',
		'',
		`Hook prompt:`,
		hook.prompt ?? ''
	].join('\n');
};

const WorkspaceEventHookConfig = ({
	hook,
	onSave,
	onDelete,
	onDryRun
}: {
	hook: EventHookDetailsState;
	onSave: (updates: Partial<EventHookDetailsState>) => void;
	onDelete?: () => void;
	onDryRun?: () => void | Promise<void>;
}) => {
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [open, setOpen] = useState(false);
	const promptRef = React.useRef<HTMLTextAreaElement>(null);
	const autoResizePrompt = React.useCallback(() => {
		const el = promptRef.current;
		if (el) {
			el.style.height = '0';
			el.style.height = Math.max(80, el.scrollHeight) + 'px';
		}
	}, []);
	const [prompt, setPrompt] = useState(hook.prompt || '');
	const [label, setLabel] = useState(hook.label || '');
	const [editingLabel, setEditingLabel] = useState(false);
	const [saved, setSaved] = useState(false);
	const dirty = prompt !== (hook.prompt || '') || label !== (hook.label || '');

	React.useEffect(() => {
		setPrompt(hook.prompt || '');
		if (!editingLabel) setLabel(hook.label || '');
	}, [hook.prompt, hook.label]);

	React.useEffect(() => {
		if (open) requestAnimationFrame(autoResizePrompt);
	}, [open, autoResizePrompt]);

	const filterSummary = (filters?: EventHookDetailsState['filters'] | string): string => {
		const parsed =
			typeof filters === 'string' ? (JSON.parse(filters) as EventHookDetailsState['filters']) : filters;
		if (!parsed?.length) return '';
		return parsed
			.map(f => {
				if (f.value == null || f.value === '') {
					if (f.operator === 'not_equals') return `${f.field} is not empty`;
					if (f.operator === 'equals') return `${f.field} is empty`;
					return `${f.field} ${FILTER_OPERATOR_LABELS[f.operator] ?? f.operator}`;
				}
				const op = FILTER_OPERATOR_LABELS[f.operator] ?? f.operator;
				return `${f.field} ${op} "${f.value}"`;
			})
			.join(' AND ');
	};

	return (
		<div className="max-w-[720px] mx-auto w-full px-5 pt-5">
			<div className="pr-sched-header">
				<div className="pr-sched-header-top">
					<button
						type="button"
						onClick={() => setOpen(!open)}
						className="pr-iconbtn"
						style={{ width: 24, height: 24 }}
					>
						{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
					</button>
					<span className="pr-row-icon" style={{ color: 'var(--pr-accent)' }}>
						<Zap />
					</span>
					{open && editingLabel ? (
						<input
							type="text"
							value={label}
							onChange={e => setLabel(e.target.value)}
							onBlur={() => setEditingLabel(false)}
							onKeyDown={e => {
								if (e.key === 'Enter') setEditingLabel(false);
							}}
							autoFocus
							className="flex-1 min-w-0 text-sm font-medium text-foreground bg-transparent border-b border-brand/30 outline-none py-0.5"
						/>
					) : (
						<span
							className="flex-1 min-w-0 text-sm font-medium text-foreground truncate cursor-pointer"
							onClick={() => {
								if (open) setEditingLabel(true);
								else setOpen(true);
							}}
						>
							{label || hook.label || 'Event hook'}
						</span>
					)}
					<div className="flex items-center gap-2 shrink-0">
						<span className="pr-sched-trigger-tag">
							{ENTITY_TYPE_LABELS[hook.entityType ?? ''] ?? hook.entityType}{' '}
							{EVENT_LABELS[hook.event ?? ''] ?? hook.event}
						</span>
						{onDryRun && (
							<button
								type="button"
								className="pr-iconbtn"
								onClick={onDryRun}
								title={T('assistant.dryRun') || 'Test run'}
							>
								<Play className="h-4 w-4" />
							</button>
						)}
						<button
							type="button"
							onClick={() => onSave({ enabled: !hook.enabled })}
							className={cn('pr-toggle', hook.enabled && 'on')}
						>
							<span className="pr-tknob" />
						</button>
						{onDelete &&
							(confirmDelete ? (
								<div className="flex items-center gap-1.5 text-xs">
									<button
										type="button"
										onClick={onDelete}
										className="px-2 py-1 rounded-md bg-danger text-white hover:bg-danger/90 cursor-pointer"
									>
										{'Delete'}
									</button>
									<button
										type="button"
										onClick={() => setConfirmDelete(false)}
										className="px-2 py-1 rounded-md text-grey-9 hover:bg-grey-2 cursor-pointer"
									>
										{T('assistant.cancel')}
									</button>
								</div>
							) : (
								<button
									type="button"
									onClick={() => setConfirmDelete(true)}
									className="h-8 w-8 rounded-lg flex items-center justify-center text-grey-9 hover:text-danger hover:bg-danger-light cursor-pointer transition-colors"
								>
									<Trash2 className="h-4 w-4" />
								</button>
							))}
					</div>
				</div>

				{open && (
					<div className="border-t border-border/40 px-4 pb-4 pt-3 flex flex-col gap-3">
						<div>
							<span className="text-xs text-grey-9 mb-1.5 block">{T('assistant.prompt')}</span>
							<textarea
								ref={promptRef}
								value={prompt}
								onChange={e => {
									setPrompt(e.target.value);
									autoResizePrompt();
								}}
								onFocus={autoResizePrompt}
								rows={1}
								className="w-full border border-grey-4 rounded-lg bg-transparent px-3 py-2 text-sm text-foreground outline-none focus:border-grey-8 resize-y min-h-[80px] transition-colors leading-relaxed"
							/>
						</div>
						{filterSummary(hook.filters) && (
							<div>
								<span className="text-xs text-grey-9 mb-1 block">{'Filters'}</span>
								<div className="text-xs text-foreground/70 bg-grey-2 rounded-lg px-3 py-2">
									{filterSummary(hook.filters)}
								</div>
							</div>
						)}
						<div className="flex items-end justify-end">
							<button
								type="button"
								disabled={!dirty && !saved}
								onClick={async () => {
									try {
										const updates: Partial<EventHookDetailsState> = {};
										if (label !== (hook.label || '')) updates.label = label;
										if (prompt !== (hook.prompt || '')) updates.prompt = prompt;
										await onSave(updates);
										setSaved(true);
										setTimeout(() => setSaved(false), 2000);
									} catch (err) {
										notifyAIWorkspaceError(err, 'saveEventHook');
									}
								}}
								className={cn(
									'h-8 px-4 rounded-md text-sm font-medium transition-colors shrink-0',
									saved
										? 'bg-green-600 text-white cursor-default'
										: dirty
										? 'bg-brand text-white hover:bg-brand/90 cursor-pointer'
										: 'bg-grey-2 text-grey-7 cursor-not-allowed'
								)}
							>
								{saved ? '✓' : T('assistant.save')}
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default WorkspaceEventHookConfig;
export { buildDryRunPrompt };
export type { EventHookDetailsState };
